import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowRight, CheckCircle, Clock, Award, Shield, BookOpen, AlertTriangle, IndianRupee, HelpCircle, Download, Layers } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExamSectionTemplate({
  sectionTitle,
  examCode,
  examFullName,
  overview,
  eligibility,
  stages,
  coreDomains,
  certificationValidity,
  recertificationPolicy,
  disclaimer,
  additionalDisclaimer,
  applyLink,
  testIdPrefix,
  fee,
  feeLabel = "Fee (India Launch Phase)",
  ipcfBadge,
  faqs = [],
}) {
  const [downloading, setDownloading] = useState(false);

  const downloadBrochure = async () => {
    setDownloading(true);
    try {
      const res = await axios.post(`${API}/brochures/exam`, {
        exam_code: examCode,
        exam_full_name: examFullName,
        section_title: sectionTitle,
        overview,
        eligibility,
        stages,
        core_domains: coreDomains,
        certification_validity: certificationValidity,
        recertification_policy: recertificationPolicy,
        disclaimer,
        fee,
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `IMCAC_${examCode}_Brochure.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Brochure downloaded');
    } catch (err) {
      toast.error('Failed to download brochure');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid={`${testIdPrefix}-page`}>
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Faculty of Aesthetic & Regenerative Medicine</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">
            {sectionTitle}
          </h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
        </div>
      </section>

      {/* Examination Model Header */}
      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="flex items-center gap-4 mb-6">
            <Badge className="bg-[#1B2A4A] text-white text-sm px-4 py-1.5">{examCode}</Badge>
            <div>
              <h2 className="text-2xl font-semibold text-[#1B2A4A] font-heading">{examFullName}</h2>
            </div>
          </div>

          {ipcfBadge && (
            <Link to="/ipcf" data-testid={`${testIdPrefix}-ipcf-badge`}>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 bg-[#C5A572]/10 border border-[#C5A572]/40 rounded-sm text-xs font-medium text-[#1B2A4A] hover:bg-[#C5A572]/20 hover:border-[#C5A572] transition-colors">
                <Layers className="h-3.5 w-3.5 text-[#C5A572]" />
                Part of the IPCF — {ipcfBadge}
                <ArrowRight className="h-3 w-3 text-[#C5A572]" />
              </span>
            </Link>
          )}

          <p className="text-base text-slate-600 leading-relaxed mb-8 max-w-4xl">{overview}</p>

          {/* Fee Display */}
          {fee && (
            <Card className="imcac-card mb-8 border-l-4 border-l-[#C5A572]" data-testid={`${testIdPrefix}-fee`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <IndianRupee className="h-6 w-6 text-[#C5A572]" />
                    <div>
                      <p className="text-sm text-slate-500 uppercase tracking-wider">{feeLabel}</p>
                      <p className="text-3xl font-bold text-[#1B2A4A] font-heading">{fee}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Button
                      variant="outline"
                      onClick={downloadBrochure}
                      disabled={downloading}
                      className="rounded-sm border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A]/5 px-6 py-2.5"
                      data-testid={`${testIdPrefix}-download-brochure-btn`}
                    >
                      <Download className="mr-2 h-4 w-4" /> {downloading ? 'Generating...' : 'Download Brochure'}
                    </Button>
                    <Link to={applyLink} data-testid={`${testIdPrefix}-apply-btn-top`}>
                      <Button className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm px-8 py-2.5">
                        Apply for {examCode} <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Eligibility */}
          <Card className="imcac-card mb-8" data-testid={`${testIdPrefix}-eligibility`}>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4">Eligibility Criteria</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {eligibility.map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Examination Stages */}
          <h3 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-6">Examination Structure</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid={`${testIdPrefix}-stages`}>
            {stages.map((stage, i) => (
              <Card key={i} className="imcac-card border-t-2 border-t-[#C5A572]">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-7 h-7 bg-[#1B2A4A] rounded-sm flex items-center justify-center">
                      <span className="text-xs font-bold text-[#C5A572]">{i + 1}</span>
                    </span>
                    <Badge variant="outline" className="text-xs rounded-sm">Stage {i + 1}</Badge>
                  </div>
                  <h4 className="text-sm font-semibold text-[#1B2A4A] mb-1">{stage.title}</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">{stage.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Core Domains */}
          <Card className="imcac-card mb-8" data-testid={`${testIdPrefix}-domains`}>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4">Core Assessment Domains</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {coreDomains.map((domain, i) => (
                  <div key={i} className="flex items-center gap-2 p-3 bg-[#F8FAFC] rounded-sm border border-slate-100">
                    <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0" />
                    <span className="text-sm text-slate-700">{domain}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Mandatory Exam Orientation */}
          <div className="mb-8 rounded-sm border border-slate-200 shadow-sm overflow-hidden" style={{backgroundColor: '#1B2A4A'}} data-testid={`${testIdPrefix}-orientation`}>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <BookOpen className="h-5 w-5 text-[#C5A572]" />
                <h3 className="text-lg font-semibold text-white font-heading">Pre-Examination Academic Orientation Program</h3>
                <Badge className="bg-[#C5A572] text-white text-xs">Included in Exam Fee</Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {[
                  'Examination blueprint explanation',
                  'OSCE structure overview',
                  'Case log requirements',
                  'Viva framework',
                  'Ethics & governance standards',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0" />
                    <span className="text-sm text-white font-medium">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-white font-medium border-t border-slate-600 pt-4 mt-4">
                Participation in orientation does not influence examination results.
              </p>
            </div>
          </div>

          {/* Certification & Recertification */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <Card className="imcac-card" data-testid={`${testIdPrefix}-validity`}>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Award className="h-5 w-5 text-[#C5A572]" />
                  <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading">Certification Validity</h3>
                </div>
                <ul className="space-y-2">
                  {certificationValidity.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card className="imcac-card" data-testid={`${testIdPrefix}-recertification`}>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="h-5 w-5 text-[#C5A572]" />
                  <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading">Recertification Policy</h3>
                </div>
                <ul className="space-y-2">
                  {recertificationPolicy.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Apply Button */}
          <div className="flex justify-center gap-3 mb-8 flex-wrap">
            <Button
              variant="outline"
              onClick={downloadBrochure}
              disabled={downloading}
              className="rounded-sm border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A]/5 px-8 py-3 text-base"
              data-testid={`${testIdPrefix}-download-brochure-btn-bottom`}
            >
              <Download className="mr-2 h-4 w-4" /> {downloading ? 'Generating...' : 'Download Brochure'}
            </Button>
            <Link to={applyLink} data-testid={`${testIdPrefix}-apply-btn`}>
              <Button className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm px-10 py-3 text-base">
                Apply for {examCode} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* Additional Disclaimer (for CBERM regulatory warning) */}
          {additionalDisclaimer && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-5 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800 mb-1">Regulatory Notice</p>
                  <p className="text-sm text-amber-700 leading-relaxed">{additionalDisclaimer}</p>
                </div>
              </div>
            </div>
          )}

          {/* Disclaimer */}
          {disclaimer && (
            <div className="bg-[#F8FAFC] border border-slate-200 rounded-sm p-5">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-600 leading-relaxed">{disclaimer}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="imcac-section bg-slate-50">
          <div className="imcac-container max-w-4xl">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#1B2A4A]/5 rounded-sm mb-3">
                <HelpCircle className="h-3 w-3 text-[#C5A572]" />
                <span className="text-xs font-semibold tracking-wider text-[#1B2A4A] uppercase">FAQs</span>
              </div>
              <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading">Frequently Asked Questions</h2>
            </div>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-slate-200">
                  <AccordionTrigger className="text-left font-semibold text-[#1B2A4A] hover:text-[#C5A572] hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}
    </div>
  );
}
